import { appwriteConfig } from '../../config/environment';
import { ACCOUNT_ACTIONS } from './account-actions';
import { functions } from './client';
import { GAMES_ACTIONS } from './games-actions';

/**
 * Error de una acción del backend.
 *
 * `code` es el código estable que devuelve la Function (`chat_not_found`,
 * `display_name_required`…). La interfaz traduce ese código; nunca muestra
 * este mensaje, que viene en inglés y es para la consola.
 */
export class CommandError extends Error {
  readonly code: string;

  constructor(message: string, code: string) {
    super(message);
    this.name = 'CommandError';
    this.code = code;
  }
}

export function isCommandError(error: unknown): error is CommandError {
  return error instanceof CommandError;
}

/**
 * Cuánto se espera a que una Function responda antes de darla por colgada.
 *
 * `functions.createExecution` del SDK de Appwrite no tiene su propio
 * timeout: si la respuesta nunca llega (un corte de red, una Function que se
 * queda esperando algo que no vuelve), la promesa no se resuelve ni se
 * rechaza nunca — la pantalla se queda en "Cargando…" para siempre y no hay
 * error que reportar (ver `report-client-error.ts`, que solo se dispara si
 * la promesa efectivamente falla). 20 s alcanza de sobra para un cold start
 * y dista de la espera indefinida que causaba el problema.
 */
const COMMAND_TIMEOUT_MS = 20_000;

export function withTimeout<T>(promise: Promise<T>, timeoutMs = COMMAND_TIMEOUT_MS): Promise<T> {
  let timer: ReturnType<typeof setTimeout>;
  const timeout = new Promise<never>((_, reject) => {
    timer = setTimeout(() => reject(new CommandError('The command timed out.', 'timeout')), timeoutMs);
  });
  return Promise.race([promise, timeout]).finally(() => clearTimeout(timer));
}

/**
 * A qué Function va una acción cuando quien llama no lo indica.
 *
 * `management-command` y `account` se repartieron un único gateway el
 * 5 sept 2026 (ver `functions.md` de `rs-functions`): antes toda acción sin
 * `functionId` explícito iba a `management-command` porque era la única.
 * Ahora este mapa decide — quien llama a `runCommand` ya no necesita saber en
 * qué Function vive cada acción, ni acordarse de actualizarlo si una acción
 * se muda de una a otra. `GAMES_ACTIONS` suma las tres acciones de dados que
 * se mudaron de `management-command` a `games` ese mismo día.
 */
function defaultFunctionIdFor(action: string): string {
  if (ACCOUNT_ACTIONS.has(action)) return appwriteConfig.accountFunctionId;
  if (GAMES_ACTIONS.has(action)) return appwriteConfig.gamesFunctionId;
  return appwriteConfig.managementFunctionId;
}

/**
 * Ejecuta una acción del backend.
 *
 * Es el único camino de la aplicación hacia las Functions: ninguna superficie
 * llama a Appwrite por su cuenta. El backend sigue siendo la autoridad sobre
 * permisos y validaciones; esto solo transporta la petición y la respuesta.
 *
 * `functionId` es para los casos que ya tienen su propia Function aparte
 * (`chat`, `media`, `games`, ...) y la pasan siempre explícita. Si se omite,
 * se resuelve entre `management-command`/`account` por el nombre de la
 * acción — ver `defaultFunctionIdFor`.
 */
export async function runCommand<T>(
  action: string,
  data: Record<string, unknown> = {},
  functionId: string = defaultFunctionIdFor(action),
): Promise<T> {
  const execution = await withTimeout(
    functions.createExecution({
      functionId,
      body: JSON.stringify({ action, ...data }),
      async: false,
    }),
  );

  let value: Record<string, unknown>;
  try {
    value = JSON.parse(execution.responseBody || '{}') as Record<string, unknown>;
  } catch {
    throw new CommandError('Invalid response body.', 'invalid_response');
  }

  if (execution.status === 'failed' || typeof value.error === 'string') {
    const message = typeof value.error === 'string' ? value.error : 'Command failed.';
    const code = typeof value.code === 'string' ? value.code : String(execution.responseStatusCode);
    throw new CommandError(message, code);
  }

  return value as T;
}
